import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Product } from "@/data/products";
import { usePaginatedProducts } from "@/hooks/usePaginatedProducts";
import { ProductCategory, fetchAllProductsForSearch, SearchProductsResult, searchProducts, getUniqueBrands, getUniqueCategories } from "@/services/productService";
import ProductCard from "@/components/ProductCard";
import ProductFilters from "@/components/ProductFilters";
import type { SortOption } from "@/components/ProductSort";
import ScrollDownFAB from "@/components/ScrollDownFAB";
import ProductPagination from "@/components/ProductPagination";
import { Button } from "@/components/ui/button";
import {
  X,
  Filter,
  Search as SearchIcon,
  AlertTriangle,
  RefreshCw,
  Wifi,
  WifiOff,
  ServerCrash,
} from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useDebouncedCallback } from 'use-debounce';
import { getProductListingSEO, validateSEOData, generateKeywords } from "@/services/seoService";
import { motion, AnimatePresence } from "framer-motion";
import FilterBottomSheet from "@/components/FilterBottomSheet";
import { useIsMobile } from "@/hooks/use-mobile";

const ProductListing = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height
  const isMobile = useIsMobile();
  
  // Sticky search/filter component state
  const [showStickySearchFilter, setShowStickySearchFilter] = useState(false);
  const [fabVisible, setFabVisible] = useState(true);
  const searchBarRef = useRef<HTMLInputElement>(null);

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  // Auto-focus search bar if navigated from navbar search icon
  useEffect(() => {
    if (location.state?.autoFocusSearch && searchBarRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        searchBarRef.current?.focus();
      }, 100);
    }
  }, [location.state]);

  // Convert URL category to ProductCategory type
  const getProductCategory = (urlCategory?: string): ProductCategory => {
    switch (urlCategory) {
      case "best-sellers":
        return "best-sellers";
      case "on-sale":
        return "on-sale";
      case "new":
        return "new";
      case "all":
      default:
        return "all";
    }
  };

  const productCategory = getProductCategory(category);

  // Use the new paginated products hook
  const {
    products: serverProducts,
    loading: serverLoading,
    error: serverError,
    totalCount,
    currentPage: serverCurrentPage,
    totalPages: serverTotalPages,
    fetchPage,
  } = usePaginatedProducts({
    category: productCategory,
    pageSize: 16,
  });

  // Client-side filtering state (applied to current server page only)
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  // Search state management
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchCache, setSearchCache] = useState<Map<ProductCategory, SearchProductsResult>>(new Map());
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false); // New state for typing indicator
  const [isSearching, setIsSearching] = useState(false); // Combined typing + fetching state

  // Filter state management (brand and price filtering)
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filterCache, setFilterCache] = useState<Map<string, SearchProductsResult>>(new Map());
  const [filterResults, setFilterResults] = useState<Product[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Search pagination state
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);
  const searchPageSize = 16;

  // Filter pagination state  
  const [filterCurrentPage, setFilterCurrentPage] = useState(1);
  const filterPageSize = 16;

  // Filter state
  const maxPriceValue = 100;
  const [priceRange, setPriceRange] = useState<[number, number]>([
    0,
    maxPriceValue,
  ]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Sorting state
  const [sortOption, setSortOption] = useState<SortOption>("featured");

  // Mobile filter visibility
  const [showFilters, setShowFilters] = useState(false);

  // Filter bottom sheet state
  const [showFilterBottomSheet, setShowFilterBottomSheet] = useState(false);
  
  // Filter options data for bottom sheet
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [errorBrands, setErrorBrands] = useState<string>('');
  const [errorCategories, setErrorCategories] = useState<string>('');

  // Fetch all products for search (with caching)
  const fetchProductsForSearch = async (category: ProductCategory): Promise<Product[]> => {
    // Check cache first
    const cached = searchCache.get(category);
    if (cached) {
      console.log(`Using cached search data for category: ${category}`);
      return cached.products;
    }

    try {
      setSearchError(null);
      
      const result = await fetchAllProductsForSearch(category);
      
      // Cache the result
      setSearchCache(prev => new Map(prev).set(category, result));
      
      return result.products;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch products for search';
      setSearchError(errorMessage);
      console.error('Search fetch error:', error);
      return [];
    }
  };

  // Helper function to normalize text by removing special characters and whitespace
  const normalizeText = (text: string): string => {
    return text.toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  // Perform client-side search with prioritization
  const performSearch = (query: string, products: Product[]): Product[] => {
    if (!query.trim()) return products;
    
    const normalizedQuery = normalizeText(query);
    const matchedProducts = new Set<string>(); // Track product IDs to prevent duplicates
    const results: Product[] = [];

    // Helper function to check if normalized text matches query
    const isMatch = (text: string): boolean => {
      return normalizeText(text).includes(normalizedQuery);
    };

    // Priority 1: Tags match
    products.forEach(product => {
      if (product.tags && product.tags.some(tag => isMatch(tag))) {
        if (!matchedProducts.has(product.id)) {
          results.push(product);
          matchedProducts.add(product.id);
        }
      }
    });

    // Priority 2: Name match
    products.forEach(product => {
      if (isMatch(product.name)) {
        if (!matchedProducts.has(product.id)) {
          results.push(product);
          matchedProducts.add(product.id);
        }
      }
    });

    // Priority 3: Brand match
    products.forEach(product => {
      if (product.brand && isMatch(product.brand)) {
        if (!matchedProducts.has(product.id)) {
          results.push(product);
          matchedProducts.add(product.id);
        }
      }
    });

    // Priority 4: Category match
    products.forEach(product => {
      if (product.category && isMatch(product.category)) {
        if (!matchedProducts.has(product.id)) {
          results.push(product);
          matchedProducts.add(product.id);
        }
      }
    });

    // Priority 5: Description match
    products.forEach(product => {
      if (isMatch(product.description)) {
        if (!matchedProducts.has(product.id)) {
          results.push(product);
          matchedProducts.add(product.id);
        }
      }
    });

    return results;
  };

  // Fetch all products for filtering (brand and price filtering with Firebase)
  const fetchProductsForFilter = async (): Promise<Product[]> => {
    // Create cache key based on filter state
    const brandFilter = selectedBrands.length > 0 ? selectedBrands.sort().join(',') : 'all';
    const categoryFilter = selectedCategories.length > 0 ? selectedCategories.sort().join(',') : 'all';
    const minPrice = priceRange[0] > 0 ? priceRange[0] : undefined;
    const maxPrice = priceRange[1] < maxPriceValue ? priceRange[1] : undefined;
    const cacheKey = `filter_${productCategory}_${brandFilter}_${categoryFilter}_${minPrice || 0}_${maxPrice || maxPriceValue}`;
    
    // Check cache first
    const cached = filterCache.get(cacheKey);
    if (cached) {
      console.log(`Using cached filter data for: ${cacheKey}`);
      return cached.products;
    }

    try {
      setFilterLoading(true);
      setFilterError(null);
      
      let allFilteredProducts: Product[] = [];

      if (selectedBrands.length > 0) {
        // For multiple brands, we need to make separate queries and combine results
        // This is because Firebase doesn't support "IN" queries with other filters efficiently
        const brandPromises = selectedBrands.map(brand => 
          searchProducts(
            '', // No search query for filtering
            productCategory,
            brand,
            minPrice,
            maxPrice
          )
        );
        
        const brandResults = await Promise.all(brandPromises);
        
        // Combine all results and remove duplicates
        const productMap = new Map<string, Product>();
        brandResults.forEach(result => {
          result.products.forEach(product => {
            // Only include if product matches ALL criteria (AND logic)
            // Brand check: product must be in selected brands
            if (selectedBrands.includes(product.brand || '')) {
              // Price check: if price filters are set, product must be in range
              const withinPriceRange = (!minPrice || product.price >= minPrice) &&
                                     (!maxPrice || product.price <= maxPrice);
              const categoryOk = selectedCategories.length === 0 || (product.category && selectedCategories.includes(product.category));
              if (withinPriceRange && categoryOk) {
                productMap.set(product.id, product);
              }
            }
          });
        });
        
        allFilteredProducts = Array.from(productMap.values());
      } else {
        // No brand filter, just price filtering
        const result = await searchProducts(
          '', // No search query for filtering
          productCategory,
          undefined, // No brand filter
          minPrice,
          maxPrice
        );
        allFilteredProducts = result.products;
      }
      // Apply category filter client-side if no brands selected or to enforce AND with other criteria
      if (selectedCategories.length > 0) {
        allFilteredProducts = allFilteredProducts.filter(p => p.category && selectedCategories.includes(p.category));
      }
      
      // Cache the result
      const cacheResult = { products: allFilteredProducts, total: allFilteredProducts.length };
      setFilterCache(prev => new Map(prev).set(cacheKey, cacheResult));
      
      return allFilteredProducts;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch products for filtering';
      setFilterError(errorMessage);
      console.error('Filter fetch error:', error);
      return [];
    } finally {
      setFilterLoading(false);
    }
  };

  // Enhanced debounced search handler with searching indicator
  const debouncedSearch = useDebouncedCallback(async (query: string) => {
    // Clear searching animation when debounce period ends
    setIsSearching(false);
    
    const trimmed = query.trim();

    // Empty query clears search
    if (!trimmed) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCurrentPage(1);
      return;
    }

    // Require a small minimum length to reduce premature searches
    if (trimmed.length < 3) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCurrentPage(1);
      return;
    }

    // Check if we have cached data first
    const cached = searchCache.get(productCategory);
    
    if (cached) {
      // We already have cache and likely already performed instant search
      // Check if we're already in search mode with results - if so, this is a no-op
      if (isSearchMode && searchResults.length > 0) {
        return;
      }
      
      // Otherwise perform the search with cached data (fallback case)
      setIsSearchMode(true);
      setSearchCurrentPage(1);
      const results = performSearch(trimmed, cached.products);
      setSearchResults(results);
      return;
    }

    // No cache - show loading state and fetch from backend
    setIsSearchMode(true);
    setSearchCurrentPage(1);

    try {
      setSearchLoading(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      const products = await fetchProductsForSearch(productCategory);
      const results = performSearch(trimmed, products);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false); // Clear API loading state
    }
  }, 3000); // 3 second debounce delay

  // Enhanced debounced filter handler
  const debouncedFilter = useDebouncedCallback(async () => {
    // Check if any filters are active
    const hasActiveFilters = selectedBrands.length > 0 || 
      selectedCategories.length > 0 ||
      priceRange[0] > 0 || 
      priceRange[1] < maxPriceValue;
    
    if (!hasActiveFilters) {
      setIsFilterMode(false);
      setFilterResults([]);
      setFilterCurrentPage(1);
      return;
    }

    setIsFilterMode(true);
    setFilterCurrentPage(1); // Reset to first page when filtering
    
    try {
      const products = await fetchProductsForFilter();
      setFilterResults(products);
    } catch (error) {
      console.error('Filter error:', error);
    }
  }, 250); // Reduced to 250ms for more responsive filtering

  // Search pagination helpers
  const getSearchPaginationInfo = () => {
    const totalResults = searchResults.length;
    const totalPages = Math.ceil(totalResults / searchPageSize);
    const startIndex = (searchCurrentPage - 1) * searchPageSize;
    const endIndex = Math.min(startIndex + searchPageSize, totalResults);
    
    return {
      totalResults,
      totalPages,
      startIndex,
      endIndex,
      hasPages: totalPages > 1
    };
  };

  const getPaginatedSearchResults = (): Product[] => {
    if (!isSearchMode) return [];
    
    const { startIndex, endIndex } = getSearchPaginationInfo();
    return searchResults.slice(startIndex, endIndex);
  };

  const handleSearchPageChange = (page: number) => {
    setSearchCurrentPage(page);
    // Scroll to top when changing search pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Track if we've processed the initial cat parameter to avoid reprocessing
  const hasProcessedInitialCat = useRef(false);

  // Check for search query and cat parameter in URL params
  useEffect(() => {
    const query = searchParams.get("search") || "";
    const catParam = searchParams.get("cat") || "";
    
    setSearchQuery(query);
    
    // Apply incoming category filter from URL if provided (only once on mount)
    if (catParam && !hasProcessedInitialCat.current) {
      hasProcessedInitialCat.current = true;
      const decoded = decodeURIComponent(catParam);
      setSelectedCategories([decoded]);
      
      // Remove the cat parameter from URL after applying the filter
      // Use setTimeout to ensure this happens after the current render cycle
      setTimeout(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("cat");
        setSearchParams(newSearchParams, { replace: true });
      }, 0);
    }
    
    // Trigger search if there's a query
    if (query) {
      debouncedSearch(query);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [searchParams, debouncedSearch]);

  // Trigger filter mode when filter state changes
  useEffect(() => {
    debouncedFilter();
  }, [selectedBrands, selectedCategories, priceRange, productCategory, debouncedFilter]);

  // Clear search and filter cache when category changes
  useEffect(() => {
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchCurrentPage(1);
    setIsFilterMode(false);
    setFilterResults([]);
    setFilterCurrentPage(1);
    setSearchQuery("");
    // Clear URL search param when category changes
    searchParams.delete('search');
    setSearchParams(searchParams, { replace: true });
  }, [productCategory]);

  // Apply client-side filters and sorting to current products
  useEffect(() => {
    if ((serverLoading && !isSearchMode && !isFilterMode) || searchLoading || filterLoading) return;

    // Determine which products to filter based on mode priority:
    // 1. Filter mode (Firebase queries for brand/price) has highest priority
    // 2. Search mode (Firebase queries + client search) has second priority  
    // 3. Server products (paginated) are fallback
    let sourceProducts: Product[];
    if (isFilterMode) {
      sourceProducts = getPaginatedFilterResults(); // Use paginated filter results
    } else if (isSearchMode) {
      sourceProducts = getPaginatedSearchResults(); // Use paginated search results
    } else {
      sourceProducts = serverProducts;
    }

    let result = [...sourceProducts];

    // In filter mode, skip client-side brand and price filtering (already applied via Firebase)
    if (!isFilterMode) {
      // Apply price filter (only if not in filter mode)
    result = result.filter(
      (product) =>
        product.price >= priceRange[0] && product.price <= priceRange[1]
    );

      // Apply brand filter (only if not in filter mode)
    if (selectedBrands.length > 0) {
      result = result.filter((product) => {
          // Only use actual brand field from Firebase
          return product.brand && selectedBrands.includes(product.brand);
        });
      }
      // Apply category filter (only if not in filter mode)
      if (selectedCategories.length > 0) {
        result = result.filter((product) => product.category && selectedCategories.includes(product.category));
      }
    }

    // Only apply client-side search filter if we have cache (for instant search)
    // If no cache, don't filter products while typing - keep them unchanged
    if (!isSearchMode && !isFilterMode && searchQuery.trim() !== "") {
      const cached = searchCache.get(productCategory);
      if (cached) {
        // We have cache, so apply client-side filtering for instant search
        const query = searchQuery.toLowerCase();
        result = result.filter(
          (product) =>
            product.name.toLowerCase().includes(query) ||
            product.description.toLowerCase().includes(query)
        );
      }
      // If no cache, don't filter - keep products unchanged while user types
    }

    // Apply sorting (always applied regardless of mode)
    switch (sortOption) {
      case "price-asc":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        result.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        result.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          }
          return 0;
        });
        break;
      case "featured":
      default:
        result.sort(
          (a, b) =>
            (b.salesCount || 0) - (a.salesCount || 0) ||
            (b.isBestSeller ? 1 : 0) - (a.isBestSeller ? 1 : 0)
        );
        break;
    }

    setFilteredProducts(result);
  }, [
    serverProducts,
    searchResults,
    filterResults,
    searchCurrentPage,
    filterCurrentPage,
    isSearchMode,
    isFilterMode,
    searchQuery,
    priceRange,
    selectedBrands,
    sortOption,
    serverLoading,
    searchLoading,
    filterLoading,
  ]);

  // Scroll detection for sticky search/filter component
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Show sticky component when FAB is no longer visible (after 200px scroll)
      const shouldShowSticky = scrollTop >= 200;
      const fabIsVisible = scrollTop < 200;
      
      setShowStickySearchFilter(shouldShowSticky);
      setFabVisible(fabIsVisible);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Keep the support button clear of the mobile sticky search/filter bar.
  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.setAttribute('data-support-sticky', showStickySearchFilter.toString());

    return () => {
      htmlElement.removeAttribute('data-support-sticky');
    };
  }, [showStickySearchFilter]);

  // Handle search button click - scroll to top and focus search
  const handleStickySearch = () => {
    // Scroll to top smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Focus search bar after scroll animation with multiple attempts and selectors
    const focusSearchBar = () => {
      // Try multiple selectors to find the search input
      const selectors = [
        '.scroll-target input[type="text"]',
        '.scroll-target form input',
        '.scroll-target .bg-transparent',
        'input[placeholder*="Search"]',
        'input[placeholder*="authentic Jamaican products"]'
      ];
      
      for (const selector of selectors) {
        const searchInput = document.querySelector(selector) as HTMLInputElement;
        if (searchInput) {
          // Ensure the element is visible and focusable
          searchInput.focus();
          // Force cursor to appear by triggering click if needed
          searchInput.click();
          console.log('Search input focused using selector:', selector);
          return true;
        }
      }
      
      console.warn('Could not find search input to focus');
      return false;
    };
    
    // Try to focus after scroll animation completes
    setTimeout(() => {
      if (!focusSearchBar()) {
        // If first attempt fails, try again after a bit longer
        setTimeout(focusSearchBar, 200);
      }
    }, 600);
  };

  // Load filter options for bottom sheet
  const loadFilterOptions = async () => {
    // Load brands
    if (availableBrands.length === 0 && !isLoadingBrands) {
      setIsLoadingBrands(true);
      setErrorBrands('');
      try {
        const brands = await getUniqueBrands();
        setAvailableBrands(brands);
      } catch (error) {
        console.error('Error loading brands:', error);
        setErrorBrands('Failed to load brands');
      } finally {
        setIsLoadingBrands(false);
      }
    }

    // Load categories
    if (availableCategories.length === 0 && !isLoadingCategories) {
      setIsLoadingCategories(true);
      setErrorCategories('');
      try {
        const categories = await getUniqueCategories();
        setAvailableCategories(categories);
      } catch (error) {
        console.error('Error loading categories:', error);
        setErrorCategories('Failed to load categories');
      } finally {
        setIsLoadingCategories(false);
      }
    }
  };

  // Handle filter bottom sheet open
  const handleOpenFilterBottomSheet = () => {
    loadFilterOptions(); // Load data when opening
    setShowFilterBottomSheet(true);
  };

  // Handle filter application from bottom sheet
  const handleApplyFilters = (filters: {
    priceRange: [number, number];
    selectedBrands: string[];
    selectedCategories: string[];
  }) => {
    // Apply filters to the main component state
    setPriceRange(filters.priceRange);
    setSelectedBrands(filters.selectedBrands);
    setSelectedCategories(filters.selectedCategories);
    
    // This will trigger the existing filter logic through useEffect
  };

  // Handle pagination - this fetches new pages from server
  const handlePageChange = async (page: number) => {
    if (isFilterMode) {
      handleFilterPageChange(page);
      return;
    }
    if (isSearchMode) {
      handleSearchPageChange(page);
      return;
    }
    await fetchPage(page);
  };

  // Enhanced search handler with searching indicator
  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    
    // Show searching indicator immediately when user starts typing
    if (trimmed && query !== searchQuery) {
      setIsSearching(true);
      
      // Check if we have cached data for instant filtering
      const cached = searchCache.get(productCategory);
      if (cached && trimmed.length >= 3) {
        // Perform instant search with cached data
        setIsSearchMode(true);
        setSearchCurrentPage(1);
        const results = performSearch(trimmed, cached.products);
        setSearchResults(results);
        setIsSearching(false); // Clear searching since we got instant results
      }
    }
    
    setSearchQuery(query);
    
    // Update URL immediately for better UX
    if (query) {
      searchParams.set("search", query);
    } else {
      searchParams.delete("search");
      setIsSearching(false); // Clear searching when query is empty
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCurrentPage(1);
    }
    setSearchParams(searchParams);
    
    // Trigger debounced search (will be no-op if cache hit above)
    debouncedSearch(query);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setPriceRange([0, maxPriceValue]);
    setSelectedBrands([]);
    setSelectedCategories([]);
    setSortOption("featured");
    setSearchQuery("");
    setIsFilterMode(false);
    setFilterResults([]);
    setFilterCurrentPage(1);
    searchParams.delete("search");
    setSearchParams(searchParams);
  };

  const toTitleCase = (value: string) =>
    value
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  // Get page title based on category and selected category filters
  const getPageTitle = () => {
    if (selectedCategories.length === 1) {
      return toTitleCase(selectedCategories[0]);
    }
    switch (category) {
      case "best-sellers":
        return "Best Sellers";
      case "on-sale":
        return "Special Offers";
      case "new":
        return "New Arrivals";
      default:
        return "All Available Products";
    }
  };

  // Calculate active filters count
  const activeFiltersCount =
    (priceRange[0] > 0 || priceRange[1] < maxPriceValue ? 1 : 0) +
    selectedBrands.length +
    selectedCategories.length;

  // Get filtered count for display
  const getFilteredCount = () => {
    if (isFilterMode) {
      const { totalResults, hasPages } = getFilterPaginationInfo();
      
      // In filter mode, show filter results with pagination info
      if (hasPages) {
        return `${totalResults} filtered results (page ${filterCurrentPage} of ${Math.ceil(totalResults / filterPageSize)})`;
      }
      return `${totalResults} filtered results`;
    }
    
    if (isSearchMode) {
      const { totalResults, hasPages } = getSearchPaginationInfo();
      
      // In search mode, show total search results (before other filters)
      if (activeFiltersCount > 0) {
        if (hasPages) {
          return `${filteredProducts.length} of ${totalResults} search results (page ${searchCurrentPage})`;
        }
        return `${filteredProducts.length} of ${totalResults} search results`;
      }
      
      if (hasPages) {
        return `${totalResults} search results (page ${searchCurrentPage} of ${Math.ceil(totalResults / searchPageSize)})`;
      }
      return `${totalResults} search results`;
    }
    
    if (searchQuery || activeFiltersCount > 0) {
      // When filters are active, show count of filtered products on current page
      // Note: This is just for current page, total filtered count across all pages is unknown
      return `${filteredProducts.length} (on this page)`;
    }
    // Show total count from server when no filters
    return totalCount.toString();
  };

  // Get current pagination info for display
  const getCurrentPaginationInfo = () => {
    if (isFilterMode) {
      const { totalPages } = getFilterPaginationInfo();
      return {
        currentPage: filterCurrentPage,
        totalPages,
        hasMore: filterCurrentPage < totalPages
      };
    } else if (isSearchMode) {
      const { totalPages } = getSearchPaginationInfo();
      return {
        currentPage: searchCurrentPage,
        totalPages,
        hasMore: searchCurrentPage < totalPages
      };
    } else {
      return {
        currentPage: serverCurrentPage,
        totalPages: serverTotalPages,
        hasMore: serverCurrentPage < serverTotalPages
      };
    }
  };

  // Get filter pagination info
  const getFilterPaginationInfo = () => {
    const totalResults = filterResults.length;
    const totalPages = Math.ceil(totalResults / filterPageSize);
    const hasPages = totalPages > 1;
    
    return {
      totalResults,
      totalPages,
      hasPages
    };
  };

  // Get paginated filter results for current page
  const getPaginatedFilterResults = (): Product[] => {
    const startIndex = (filterCurrentPage - 1) * filterPageSize;
    const endIndex = startIndex + filterPageSize;
    return filterResults.slice(startIndex, endIndex);
  };

  // Handle filter page change
  const handleFilterPageChange = (page: number) => {
    setFilterCurrentPage(page);
  };

  const getFlagBackgroundColor = (index: number): string => {
    const position = (index % 16) + 1; // 1-based index within a 4x4 grid

    const yellowPositions = [1, 4, 6, 7, 10, 11, 13, 16];
    const blackPositions = [5, 8, 9, 12];

    if (yellowPositions.includes(position)) {
      return 'bg-baby-jamaica-yellow';
    }
    if (blackPositions.includes(position)) {
      return 'bg-baby-jamaica-black';
    }
    return 'bg-baby-jamaica-green'; // The rest are green
  };

  // If loading, show enhanced loading state (but not during search operations to keep products visible)
  if ((serverLoading && !isSearchMode && !isFilterMode) || filterLoading) {
    const loadingMessage = filterLoading ? "Filtering products..." : "Loading products...";
    const loadingIcon = filterLoading ? "🔍" : "🛍️";
    
    return (
      <div 
        className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Gradient Blobs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-jamaica-green/3 to-jamaica-yellow/3 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-r from-jamaica-yellow/4 to-jamaica-green/3 rounded-full mix-blend-multiply filter blur-2xl animate-pulse delay-1500"></div>
        </div>

        <div className="relative text-center max-w-md mx-auto">
          {/* Main Loading Icon Container */}
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-3xl flex items-center justify-center shadow-2xl mx-auto transform hover:scale-105 transition-transform duration-500">
              <LoadingSpinner size="lg" className="text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce shadow-lg">
              <span className="text-lg">{loadingIcon}</span>
            </div>
            
            {/* Pulse Rings */}
            <div className="absolute inset-0 rounded-3xl bg-jamaica-green/20 animate-ping"></div>
            <div className="absolute inset-2 rounded-2xl bg-jamaica-green/10 animate-ping delay-75"></div>
          </div>

          {/* Loading Content */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/40 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {loadingMessage}
            </h2>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              {filterLoading 
                ? 'We\'re applying your filters to find the perfect products for you.'
                : searchLoading 
                ? 'We\'re searching through our amazing collection of Jamaican products.'
                : 'We\'re loading our curated collection of authentic Jamaican products.'
              }
            </p>

            {/* Progress Animation */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-jamaica-green to-jamaica-yellow rounded-full animate-pulse transform origin-left">
                <div className="h-full bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green animate-pulse"></div>
              </div>
            </div>

            {/* Loading Steps */}
            <div className="flex justify-center space-x-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-jamaica-green/30 rounded-full animate-pulse"
                  style={{ animationDelay: `${i * 0.2}s` }}
                ></div>
              ))}
            </div>
          </div>

          {/* Fun Loading Text */}
          <div className="mt-6">
            <p className="text-sm text-gray-500 animate-pulse">
              🌴 Bringing Jamaica to your doorstep...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If there's an error, show error message
  if (serverError || searchError || filterError) {
    const errorType = serverError ? 'server' : filterError ? 'filter' : 'search';
    const errorMessage = serverError || filterError || searchError;
    
    return (
      <div 
        className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <div className="w-full max-w-lg mx-auto">
          {/* Error Icon Container */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                {errorType === 'server' ? (
                  <ServerCrash className="w-10 h-10 text-red-600" />
                ) : (
                  <WifiOff className="w-10 h-10 text-red-600" />
                )}
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>

          {/* Error Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {errorType === 'server' ? 'Server Error' : errorType === 'filter' ? 'Filter Error' : 'Search Error'}
            </h2>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              {errorType === 'server' 
                ? 'We encountered an issue loading the products. This might be a temporary problem with our servers.'
                : errorType === 'filter'
                ? 'We encountered an issue while filtering products. This might be a temporary problem with our database.'
                : 'We encountered an issue while searching. This might be a temporary network problem.'
              }
            </p>
            
            {/* Error Details */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border-l-4 border-red-400">
              <p className="text-sm text-gray-700 font-mono break-words">
                {errorMessage}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                className="w-full bg-gradient-to-r from-jamaica-green to-green-600 hover:from-jamaica-green/90 hover:to-green-600/90 text-white font-semibold py-3 transition-all duration-300 transform hover:scale-105 shadow-lg"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              
              <Button
                variant="outline"
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              If the problem persists, please{" "}
              <a 
                href="mailto:support@jampackd.store?subject=Website%20Error%20Report&body=Hello%20JamPack'd%20Support%20Team,%0D%0A%0D%0AI%20encountered%20an%20error%20on%20your%20website:%0D%0A%0D%0AError%20Details:%0D%0A%0D%0APlease%20help%20me%20resolve%20this%20issue.%0D%0A%0D%0AThank%20you!" 
                className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200"
              >
                contact our support team
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Generate dynamic SEO data based on current state
  const currentSearchQuery = searchParams.get('search') || '';
  const seoData = validateSEOData(getProductListingSEO(
    category === 'all' ? undefined : category,
    currentSearchQuery || undefined
  ));

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && (
          <meta name="keywords" content={generateKeywords(seoData.keywords)} />
        )}
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoData.url} />
        
        {/* Structured Data - JSON-LD Schema */}
        {seoData.schema && seoData.schema.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      
      <div 
        className="flex flex-col"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
      <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Diagonal Lines Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(45deg, rgba(0, 116, 47, 0.1) 1px, transparent 1px),
                linear-gradient(-45deg, rgba(255, 201, 1, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px, 120px 120px',
              backgroundPosition: '0 0, 40px 40px'
            }}></div>
          </div>
          
          {/* Gradient Blobs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-jamaica-green/3 to-jamaica-yellow/3 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-r from-jamaica-yellow/4 to-jamaica-green/3 rounded-full mix-blend-multiply filter blur-2xl animate-pulse delay-1500"></div>
        </div>
        
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 ${isMobile ? 'pb-20' : 'pb-12'}`}>
          {/* Enhanced Hero Section */}
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
              <div className="relative">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6 sm:hover:-rotate-6 transition-transform duration-500">
                  <SearchIcon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                  <span className="text-[10px] sm:text-xs font-bold text-white">{(() => {
                    switch (category) {
                      case "best-sellers": return "★";
                      case "on-sale": return "%";
                      case "new": return "+";
                      default: return "🛍";
                    }
                  })()}</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-xs sm:text-sm font-bold text-jamaica-black">
                  {(() => {
                    switch (category) {
                      case "best-sellers": return "Everyone Loves";
                      case "on-sale": return "Discounted Prices";
                      case "new": return "Recently Added";
                      default: return "All Products";
                    }
                  })()}
                </span>
              </div>
            </div>

            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4 leading-tight">
              <span className="bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green bg-clip-text text-transparent p-1">
                {getPageTitle()}
              </span>
            </h1>
            <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover 
              <span className="text-jamaica-yellow font-semibold"> authentic Jamaican flavours</span> and 
              <span className="text-jamaica-green font-semibold"> cultural treasures</span> that bring the island vibes to your doorstep
            </p>
            <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
              Discover authentic Jamaican flavours and cultural treasures.
            </p>
          </div>

          {/* Enhanced Search and Filter Bar */}
          <div className="mb-10 relative group scroll-target">
            {/* Glass Morphism Card */}
            <div className="relative backdrop-blur-sm bg-white/70 sm:bg-white/80 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/40 p-4 sm:p-6 hover:shadow-2xl transition-all duration-500 sm:hover:scale-[1.01] transform-gpu">
              
              {/* Animated Border Accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
              
              <div className="flex flex-col gap-3 sm:gap-4">
                {/* Desktop: Search Bar with inline Filter and Sort */}
                {!isMobile && (
                  <div className="flex gap-4 items-center">
                  {/* Enhanced Search Bar */}
                  <div className="flex-1 relative">
                    <div className="relative h-14 border-2 border-gray-200 hover:border-jamaica-green/30 focus-within:border-jamaica-green/50 rounded-xl shadow-sm transition-all duration-300 bg-white overflow-hidden">
                      <SearchBar
                        onSearch={handleSearch}
                        value={searchQuery}
                        placeholder="Search for authentic Jamaican products..."
                        className="h-full [&>div]:h-full [&>div]:border-0 [&>div]:rounded-xl [&>div]:bg-transparent [&>div]:shadow-none [&_input]:h-full [&_input]:bg-transparent [&_input]:font-medium"
                      />
                    </div>
                    
                    {/* Searching Indicator */}
                    {isSearching && searchQuery.trim() && (
                      <div className="absolute top-full mt-2 left-0 right-0 flex items-center gap-2 text-xs text-jamaica-green/70 animate-fade-in">
                        <div className="flex space-x-1">
                          <div className="w-1 h-1 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-1 h-1 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-1 h-1 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                        <span className="font-medium">Searching...</span>
                      </div>
                    )}
                  </div>

                  {/* Desktop Filter Button */}
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    className={`flex items-center gap-2 h-14 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-0.5 ${
                      showFilters 
                        ? "bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white shadow-lg border-2 border-transparent" 
                        : "border-2 border-gray-200 hover:border-jamaica-green/30 hover:bg-jamaica-green/5"
                    }`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    {showFilters ? (
                      <X className="h-4 w-4" />
                    ) : (
                      <Filter className="h-4 w-4" />
                    )}
                    <span>{showFilters ? "Hide Filters" : "Filters"}</span>
                    {activeFiltersCount > 0 && !showFilters && (
                      <Badge className="ml-1 bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white border-0 shadow-md">
                        {activeFiltersCount}
                      </Badge>
                    )}
                  </Button>

                  {/* Desktop Sort Products - commented out */}
                  {false && (
                    <div>
                      {/* <ProductSort
                        sortOption={sortOption}
                        setSortOption={setSortOption}
                        className="h-14"
                      /> */}
                    </div>
                  )}
                  </div>
                )}

                {/* Mobile: Separate Layout */}
                {isMobile && (
                  <div className="flex flex-col gap-3">
                  {/* Mobile Search Bar with inline compact Filter button */}
                  <div className="w-full flex items-center gap-2">
                    <div className="flex-1 relative">
                      <div className="relative h-12 border border-gray-200 sm:border-2 hover:border-jamaica-green/30 focus-within:border-jamaica-green/50 rounded-lg shadow-sm transition-all duration-300 bg-white overflow-hidden">
                        <SearchBar
                          onSearch={handleSearch}
                          value={searchQuery}
                          placeholder="Search for products..."
                          className="h-full [&>div]:h-full [&>div]:border-0 [&>div]:rounded-xl [&>div]:bg-transparent [&>div]:shadow-none [&_input]:h-full [&_input]:bg-transparent [&_input]:font-medium"
                        />
                      </div>
                      
                      {/* Searching Indicator */}
                      {isSearching && searchQuery.trim() && (
                        <div className="absolute top-full mt-1 sm:mt-2 left-0 right-0 z-20 pointer-events-none flex items-center gap-2 text-xs text-jamaica-green/70 animate-fade-in bg-white/80 backdrop-blur-sm rounded-md px-2 py-1 shadow-sm">
                          <div className="flex space-x-1">
                            <div className="w-1 h-1 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-1 h-1 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-1 h-1 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                          <span className="font-medium">Searching...</span>
                        </div>
                      )}
                    </div>

                    <Button
                      aria-label="Toggle filters"
                      variant={activeFiltersCount > 0 ? "default" : "outline"}
                      size="icon"
                      onClick={handleOpenFilterBottomSheet}
                      className={`h-10 w-10 rounded-lg transition-all duration-300 shrink-0 ${
                        activeFiltersCount > 0
                          ? "bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white shadow-md border-2 border-transparent" 
                          : "border-2 border-gray-200 hover:border-jamaica-green/30 hover:bg-jamaica-green/5"
                      }`}
                    >
                      <Filter className="h-4 w-4" />
                      {activeFiltersCount > 0 && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-jamaica-yellow rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-white">{activeFiltersCount}</span>
                        </div>
                      )}
                    </Button>
                  </div>
                  </div>
                )}
              </div>

              {/* Enhanced Products Count Indicator */}
              <div className="mt-4 sm:mt-6 flex items-center justify-between bg-gradient-to-r from-gray-50/80 to-white/80 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-100/50">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-jamaica-green rounded-full animate-pulse mt-1 flex-shrink-0"></div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-bold text-jamaica-green text-sm sm:text-base">
                      {getFilteredCount()}
                    </span>{" "}
                    {(() => {
                      if (isSearchMode) {
                        return "";
                      }
                      const count = searchQuery || activeFiltersCount > 0 
                        ? filteredProducts.length 
                        : totalCount;
                      if (activeFiltersCount > 0) {
                        return "";
                      }
                      return count === 1 ? "product available" : "products available";
                    })()}
                  </p>
                </div>

                {/* Enhanced Reset Button */}
                {(activeFiltersCount > 0 || searchQuery) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetFilters}
                    className="text-jamaica-green hover:text-jamaica-green/90 hover:bg-jamaica-green/10 font-semibold px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset filters
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-8">
            {/* Enhanced Filter Sidebar - Only render on desktop when filters are shown */}
            {showFilters && (
              <aside className="hidden md:block md:w-64 lg:w-72 shrink-0 transition-all duration-300 ease-in-out">
                <div className="relative group">
                  {/* Glass Morphism Filter Card */}
                  <div className="relative backdrop-blur-sm bg-white/70 sm:bg-white/80 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/40 p-4 sm:p-6 hover:shadow-2xl transition-all duration-500 md:sticky md:top-24">
                    
                    {/* Animated Border Accent */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
                    
                    {/* Filter Header */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-xl flex items-center justify-center shadow-lg">
                        <Filter className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-lg font-bold text-gray-900">Filter Products</span>
                    </div>
                    
                    <ProductFilters
                      priceRange={priceRange}
                      setPriceRange={setPriceRange}
                      selectedBrands={selectedBrands}
                      setSelectedBrands={setSelectedBrands}
                      selectedCategories={selectedCategories}
                      setSelectedCategories={setSelectedCategories}
                      maxPrice={maxPriceValue}
                      onReset={handleResetFilters}
                    />
                  </div>
                </div>
              </aside>
            )}

            {/* Product grid */}
            <div className="flex-1 pb-8 sm:pb-0 relative">
              {/* Search Loading Overlay */}
              {searchLoading && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-2xl">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl mx-auto mb-4 animate-pulse">
                      <SearchIcon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Searching Products</h3>
                    <p className="text-sm text-gray-600">Finding the best matches for you...</p>
                    <div className="flex justify-center mt-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-jamaica-green/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {filteredProducts.length === 0 ? (
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 opacity-50"></div>
                  
                  <div className="relative text-center py-20 px-8">
                    {/* Icon Container */}
                    <div className="flex justify-center mb-6">
                      <div className="relative">
                        <div className="w-24 h-24 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/20 rounded-full flex items-center justify-center shadow-lg">
                          <SearchIcon className="w-12 h-12 text-jamaica-green/70" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                          <span className="text-jamaica-black text-xs font-bold">?</span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {searchQuery || activeFiltersCount > 0
                        ? "No Products Found"
                        : "No Products Available"}
                    </h3>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed max-w-md mx-auto">
                      {searchQuery || activeFiltersCount > 0
                        ? `We couldn't find any products matching your ${searchQuery ? 'search' : 'filter'} criteria on this page. Try adjusting your filters or search terms.`
                        : "We're working on adding more amazing Jamaican products. Check back soon!"}
                    </p>

                    {/* Action Buttons */}
                    {searchQuery || activeFiltersCount > 0 ? (
                      <div className="space-y-3 max-w-xs mx-auto">
                        <Button
                          onClick={handleResetFilters}
                          className="w-full bg-gradient-to-r from-jamaica-green to-green-600 hover:from-jamaica-green/90 hover:to-green-600/90 text-white font-semibold py-3 transition-all duration-300 transform hover:scale-105 shadow-lg"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Reset All Filters
                        </Button>
                        
                        <Button
                          variant="outline"
                          onClick={() => {
                            if (isSearchMode) {
                              setSearchQuery("");
                              searchParams.delete('search');
                              setSearchParams(searchParams);
                              setIsSearchMode(false);
                              setSearchResults([]);
                              setSearchCurrentPage(1);
                            }
                          }}
                          className="w-full border-jamaica-green text-jamaica-green hover:bg-jamaica-green/5 transition-all duration-300"
                        >
                          Browse All Products
                        </Button>
                        
                        <p className="text-sm text-gray-500 mt-3">
                          or try browsing other pages
                        </p>
                      </div>
                    ) : (
                      <div className="max-w-xs mx-auto">
                        <Button
                          variant="outline"
                          onClick={() => window.location.href = '/'}
                          className="w-full border-jamaica-green text-jamaica-green hover:bg-jamaica-green/5 transition-all duration-300"
                        >
                          Explore Our Collections
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
                  {filteredProducts.map((product, index) => (
                    <ProductCard
                      key={`${product.id}-${index}`}
                      product={product}
                      flagBackgroundColor={getFlagBackgroundColor(index)}
                      className="group h-full"
                      delay={index * 50}
                    />
                  ))}
                </div>
              )}

              {/* Pagination Controls */}
              {(() => {
                const paginationInfo = getCurrentPaginationInfo();
                return paginationInfo.totalPages > 1 && (
                  <ProductPagination
                    currentPage={paginationInfo.currentPage}
                    totalPages={paginationInfo.totalPages}
                    onPageChange={handlePageChange}
                    className="mt-8"
                  />
                );
              })()}

              {/* Mode-specific info */}
              {isFilterMode && (
                <div className="mt-8 text-center">
                  <p className="text-sm text-gray-500">
                    Showing filtered results in {getPageTitle()}
                    {getFilterPaginationInfo().hasPages && ` (${getFilterPaginationInfo().totalPages} pages)`}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 border-jamaica-green text-jamaica-green hover:bg-jamaica-green/5"
                    onClick={handleResetFilters}
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}

              {/* Search mode info */}
              {isSearchMode && (
                <div className="mt-8 text-center">
                  <p className="text-sm text-gray-500">
                    Showing all search results for "{searchQuery}" in {getPageTitle()}
                    {getSearchPaginationInfo().hasPages && ` (${getSearchPaginationInfo().totalPages} pages)`}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 border-jamaica-green text-jamaica-green hover:bg-jamaica-green/5"
                    onClick={() => {
                      setSearchQuery("");
                      searchParams.delete('search');
                      setSearchParams(searchParams);
                      setIsSearchMode(false);
                      setSearchResults([]);
                      setSearchCurrentPage(1);
                    }}
                  >
                    Clear Search
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Scroll Down FAB - Only visible when product list is not empty and within scroll threshold */}
      {filteredProducts.length > 0 && fabVisible && (
        <ScrollDownFAB 
          scrollTarget=".scroll-target" 
          tooltipText="Scroll to see more products"
          className={`${showStickySearchFilter ? (isMobile ? '!bottom-[7.5rem]' : '!bottom-8') : (isMobile ? '!bottom-24' : '!bottom-8')}`}
        />
      )}

      {/* Sticky Search/Filter Component - Mobile Only */}
      <AnimatePresence>
        {showStickySearchFilter && isMobile && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-[4.5rem] left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-30"
          >
            <div className="flex items-center gap-3 p-4">
              {/* Search Button */}
              <Button
                onClick={handleStickySearch}
                className="flex-1 bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white font-semibold py-3 shadow-md hover:shadow-lg transition-all duration-200"
              >
                <SearchIcon className="h-4 w-4 mr-2" />
                Search
              </Button>
              
              {/* Filter Button */}
              <Button
                onClick={handleOpenFilterBottomSheet}
                variant={activeFiltersCount > 0 ? "default" : "outline"}
                className={`px-6 py-3 font-semibold transition-all duration-200 ${
                  activeFiltersCount > 0
                    ? "bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white shadow-md border-2 border-transparent"
                    : "border-2 border-gray-200 hover:border-jamaica-green/30 hover:bg-jamaica-green/5"
                }`}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filter
                {activeFiltersCount > 0 && (
                  <Badge className="ml-2 bg-white/20 text-white border-0">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter Bottom Sheet */}
      <FilterBottomSheet
        isOpen={showFilterBottomSheet}
        onClose={() => setShowFilterBottomSheet(false)}
        onApply={handleApplyFilters}
        initialPriceRange={priceRange}
        initialSelectedBrands={selectedBrands}
        initialSelectedCategories={selectedCategories}
        maxPrice={maxPriceValue}
        availableBrands={availableBrands}
        availableCategories={availableCategories}
        isLoadingBrands={isLoadingBrands}
        isLoadingCategories={isLoadingCategories}
        errorBrands={errorBrands}
        errorCategories={errorCategories}
      />
    </div>
    </>
  );
};

export default ProductListing;
